import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Falls back to localhost only for local dev; set REACT_APP_API_BASE_URL in
// production (.env / build config) so this actually points at your real API.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";
const API_URL = `${API_BASE_URL}/jobs/public/`;

const STATUS_STYLES = {
  pending:   { bg: '#fffde0', color: '#8a7f00', border: '#fff000' },
  accepted:  { bg: '#f2f2f2', color: '#000000', border: '#cccccc' },
  completed: { bg: '#f2f2f2', color: '#000000', border: '#cccccc' },
  default:   { bg: '#f1f3f5', color: '#6c757d', border: '#e0e0e0' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.default;
  return (
    <span
      style={{
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        borderRadius: 20, padding: '3px 13px', fontSize: '.7rem',
        fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap',
      }}
    >
      {status || 'open'}
    </span>
  );
}

function JobCard({ job }) {
  return (
    <div className="kk-job-card">
      <div className="kk-job-card-body">
        <div className="kk-job-top">
          <span className="kk-job-service-tag">{job.service || "General Service"}</span>
          <StatusPill status={job.status}/>
        </div>
        <h2 className="kk-job-title">{job.service || 'General Service'}</h2>
        <p className="kk-job-desc">
          {job.description ? job.description.slice(0, 120) : "No description available."}
          {job.description && job.description.length > 120 ? '…' : ''}
        </p>
        <div className="kk-job-meta">
          <span><i className="fas fa-map-marker-alt"/>{job.location || "Not specified"}</span>
          <span className="kk-job-budget"><i className="fas fa-coins"/>{job.budget || "Negotiable"}</span>
        </div>
      </div>
      <div className="kk-job-card-foot">
        <Link to={`/jobs/${job.id}`} className="kk-job-btn">
          View Details <i className="fas fa-arrow-right ms-1"/>
        </Link>
      </div>
    </div>
  );
}

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    let results = jobs;

    if (search) {
      results = results.filter(
        (job) =>
          job.service?.toLowerCase().includes(search.toLowerCase()) ||
          job.description?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (location) {
      results = results.filter((job) =>
        job.location?.toLowerCase().includes(location.toLowerCase())
      );
    }

    setFilteredJobs(results);
  }, [search, location, jobs]);

  const fetchJobs = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error("Failed to load jobs");
      }

      const data = await response.json();

      setJobs(data);
      setFilteredJobs(data);
    } catch (error) {
      console.error('JobsPage fetch failed:', error);
      setLoadError(true);
      setJobs([]);
      setFilteredJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => { setSearch(''); setLocation(''); setActiveCategory('all'); };

  // ── Group jobs by service category ──
  const grouped = filteredJobs.reduce((acc, job) => {
    const cat = job.service?.trim() || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(job);
    return acc;
  }, {});

  const categories = Object.keys(grouped).sort((a, b) => grouped[b].length - grouped[a].length);
  const visibleCategories = activeCategory === 'all' ? categories : categories.filter(c => c === activeCategory);

  return (
    <div className="kk-jobs-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800;900&display=swap');
        :root{
          --green:#000000; --green-d:#1a1a1a; --green-l:#f2f2f2;
          --gold:#fff000; --gold-d:#e0d800; --gold-l:#fffde0;
          --bg:#ffffff; --text:#000000; --muted:#4d4d4d;
        }
        .kk-jobs-page{background:var(--bg);min-height:100vh;font-family:'Raleway',sans-serif;color:var(--text);}

        .kk-jobs-hero{background:linear-gradient(135deg,var(--green-d) 0%,var(--green) 100%);padding:64px 24px 90px;text-align:center;position:relative;overflow:hidden;}
        .kk-jobs-hero::before{content:'';position:absolute;top:-70px;right:-70px;width:240px;height:240px;border-radius:50%;background:rgba(255,240,0,.1);}
        .kk-jobs-hero-eyebrow{display:inline-flex;align-items:center;gap:7px;background:rgba(255,240,0,.16);border:1px solid rgba(255,240,0,.4);color:var(--gold);border-radius:20px;padding:5px 14px;font-size:.72rem;font-weight:700;letter-spacing:.05em;text-transform:uppercase;margin-bottom:16px;position:relative;z-index:1;}
        .kk-jobs-hero h1{font-family:'Raleway',sans-serif;font-weight:800;font-size:clamp(1.8rem,4vw,2.8rem);color:#fff;margin:0 0 12px;position:relative;z-index:1;}
        .kk-jobs-hero p{color:rgba(255,255,255,.82);font-size:1rem;margin:0;position:relative;z-index:1;}

        .kk-jobs-search{max-width:1100px;margin:-52px auto 0;padding:0 20px;position:relative;z-index:2;}
        .kk-jobs-search-card{background:#fff;border-radius:16px;box-shadow:0 12px 40px rgba(0,0,0,.12);padding:22px;display:flex;gap:14px;flex-wrap:wrap;border:1.5px solid #e5e5e5;}
        .kk-jobs-input-wrap{flex:1;min-width:200px;position:relative;}
        .kk-jobs-input-wrap i{position:absolute;top:50%;left:16px;transform:translateY(-50%);color:#a8a29e;font-size:.9rem;}
        .kk-jobs-input{width:100%;border:1.5px solid #e7e5e4;border-radius:12px;padding:12px 14px 12px 40px;font-family:'Raleway',sans-serif;font-size:.92rem;color:var(--text);transition:border-color .18s,box-shadow .18s;}
        .kk-jobs-input:focus{outline:none;border-color:var(--gold);box-shadow:0 0 0 4px rgba(255,240,0,.22);}
        .kk-jobs-clear{background:#f1f3f5;color:var(--muted);border:1.5px solid #e0e0e0;border-radius:12px;padding:0 20px;font-weight:600;font-size:.85rem;cursor:pointer;transition:all .15s;white-space:nowrap;}
        .kk-jobs-clear:hover{background:#fee2e2;color:#dc2626;border-color:#fca5a5;}

        /* ── CATEGORY FILTER BAR ── */
        .kk-cat-bar-wrap{max-width:1100px;margin:24px auto 0;padding:0 20px;}
        .kk-cat-bar{display:flex;gap:10px;overflow-x:auto;padding:4px 2px 14px;-webkit-overflow-scrolling:touch;}
        .kk-cat-bar::-webkit-scrollbar{height:6px;}
        .kk-cat-bar::-webkit-scrollbar-thumb{background:#e0e0e0;border-radius:10px;}
        .kk-cat-chip{flex-shrink:0;display:flex;align-items:center;gap:7px;background:#fff;border:1.5px solid #e7e5e4;color:var(--text);border-radius:30px;padding:8px 18px;font-size:.83rem;font-weight:600;cursor:pointer;transition:all .18s;white-space:nowrap;font-family:'Raleway',sans-serif;}
        .kk-cat-chip:hover{border-color:var(--gold);}
        .kk-cat-chip.active{background:var(--gold);border-color:var(--gold);color:#000000;}
        .kk-cat-chip .count{background:rgba(0,0,0,.08);border-radius:20px;padding:1px 8px;font-size:.72rem;font-weight:700;}
        .kk-cat-chip.active .count{background:rgba(0,0,0,.15);}

        .kk-jobs-body{max-width:1100px;margin:0 auto;padding:6px 20px 80px;}

        /* ── CATEGORY SECTIONS ── */
        .kk-cat-section{margin-bottom:44px;}
        .kk-cat-section-head{display:flex;align-items:baseline;gap:12px;margin-bottom:18px;padding-bottom:12px;border-bottom:2px solid #e5e5e5;}
        .kk-cat-section-title{font-family:'Raleway',sans-serif;font-weight:800;font-size:1.35rem;color:var(--text);margin:0;}
        .kk-cat-section-count{background:var(--green-l);color:var(--text);border:1px solid #d0d0d0;border-radius:20px;padding:3px 13px;font-size:.78rem;font-weight:700;}

        .kk-jobs-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:22px;}
        .kk-job-card{background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,.07);border:1.5px solid #e5e5e5;display:flex;flex-direction:column;transition:transform .25s,box-shadow .25s,border-color .2s;position:relative;}
        .kk-job-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(to right,var(--green),var(--gold));transform:scaleX(0);transform-origin:left;transition:transform .3s;}
        .kk-job-card:hover{transform:translateY(-5px);box-shadow:0 14px 36px rgba(0,0,0,.12);border-color:var(--gold);}
        .kk-job-card:hover::before{transform:scaleX(1);}
        .kk-job-card-body{padding:22px;display:flex;flex-direction:column;gap:14px;flex:1;}
        .kk-job-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;}
        .kk-job-service-tag{background:var(--green-l);color:var(--text);border:1px solid #d0d0d0;border-radius:20px;padding:4px 13px;font-size:.75rem;font-weight:700;}
        .kk-job-title{font-family:'Raleway',sans-serif;font-size:1.15rem;font-weight:800;color:var(--text);margin:0;}
        .kk-job-desc{font-size:.85rem;color:var(--muted);line-height:1.65;margin:0;flex:1;}
        .kk-job-meta{display:flex;flex-direction:column;gap:7px;font-size:.83rem;color:#333333;border-top:1px solid #f0f0f0;padding-top:12px;}
        .kk-job-meta span{display:flex;align-items:center;gap:7px;}
        .kk-job-meta i{color:var(--gold-d);width:14px;flex-shrink:0;}
        .kk-job-meta .kk-job-budget{color:var(--text);font-weight:700;}
        .kk-job-card-foot{padding:0 22px 22px;}
        .kk-job-btn{display:block;text-align:center;background:var(--gold);color:#000000;border:none;border-radius:10px;padding:12px 0;font-weight:700;font-size:.88rem;text-decoration:none;transition:all .18s;}
        .kk-job-btn:hover{background:var(--gold-d);color:#000000;transform:translateY(-1px);}

        .kk-jobs-empty,.kk-jobs-loading{text-align:center;padding:80px 20px;background:#fff;border-radius:16px;border:1.5px solid #e5e5e5;}
        .kk-jobs-empty h2,.kk-jobs-loading p{font-family:'Raleway',sans-serif;}
        .kk-jobs-empty h2{font-size:1.3rem;font-weight:800;color:var(--text);margin:0 0 8px;}
        .kk-jobs-empty p{color:var(--muted);font-size:.9rem;margin:0 0 16px;}
        .kk-jobs-retry{background:var(--gold);color:#000000;border:none;border-radius:10px;padding:10px 22px;font-weight:700;font-size:.86rem;cursor:pointer;transition:background .18s;}
        .kk-jobs-retry:hover{background:var(--gold-d);}
        .kk-spinner{width:44px;height:44px;border-radius:50%;border:4px solid var(--green-l);border-top-color:var(--gold);animation:kk-spin .8s linear infinite;margin:0 auto 16px;}
        @keyframes kk-spin{to{transform:rotate(360deg);}}

        @media(max-width:576px){
          .kk-jobs-hero{padding:48px 20px 80px;}
          .kk-jobs-search-card{flex-direction:column;}
          .kk-jobs-clear{width:100%;}
          .kk-cat-section-head{flex-wrap:wrap;}
        }
      `}</style>

      {/* Hero */}
      <div className="kk-jobs-hero">
        <h1>Find Skilled Craftsmen Jobs</h1>
        <p>Discover verified jobs from clients across Kenya.</p>
      </div>

      {/* Search */}
      <div className="kk-jobs-search">
        <div className="kk-jobs-search-card">
          <div className="kk-jobs-input-wrap">
            <i className="fas fa-search"/>
            <input
              type="text"
              placeholder="Search by service…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="kk-jobs-input"
            />
          </div>
          <div className="kk-jobs-input-wrap">
            <i className="fas fa-map-marker-alt"/>
            <input
              type="text"
              placeholder="Location…"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="kk-jobs-input"
            />
          </div>
          {(search || location || activeCategory !== 'all') && (
            <button className="kk-jobs-clear" onClick={clearFilters}>
              <i className="fas fa-times me-1"/>Clear
            </button>
          )}
        </div>
      </div>

      {/* Category filter chips */}
      {!loading && !loadError && categories.length > 0 && (
        <div className="kk-cat-bar-wrap">
          <div className="kk-cat-bar">
            <button
              className={`kk-cat-chip ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              <i className="fas fa-th-large"/> All
              <span className="count">{filteredJobs.length}</span>
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`kk-cat-chip ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
                <span className="count">{grouped[cat].length}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Jobs, grouped by category */}
      <div className="kk-jobs-body">
        {loading ? (
          <div className="kk-jobs-loading">
            <div className="kk-spinner"/>
            <p style={{color:'var(--muted)',fontWeight:600}}>Loading jobs…</p>
          </div>
        ) : loadError ? (
          <div className="kk-jobs-empty">
            <span style={{fontSize:'2.5rem',display:'block',marginBottom:14}}>⚠️</span>
            <h2>Couldn't Load Jobs</h2>
            <p>We couldn't reach the server. Check your connection and try again.</p>
            <button className="kk-jobs-retry" onClick={fetchJobs}>
              <i className="fas fa-rotate-right me-2"/>Try Again
            </button>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="kk-jobs-empty">
            <span style={{fontSize:'2.5rem',display:'block',marginBottom:14}}>🔍</span>
            <h2>No Jobs Available</h2>
            <p>Try a different search term or location.</p>
          </div>
        ) : (
          visibleCategories.map((cat) => (
            <div className="kk-cat-section" key={cat}>
              <div className="kk-cat-section-head">
                <h2 className="kk-cat-section-title">{cat}</h2>
                <span className="kk-cat-section-count">
                  {grouped[cat].length} job{grouped[cat].length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="kk-jobs-grid">
                {grouped[cat].map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
